const MARKET_TIME_ZONE = 'America/New_York';
const MARKET_OPEN_MINUTES = 9 * 60 + 30;
const REGULAR_MARKET_CLOSE_MINUTES = 16 * 60;
const EARLY_MARKET_CLOSE_MINUTES = 13 * 60;
const SESSION_DATE_LOOKBACK_DAYS = 90;
const marketDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: MARKET_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const marketTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: MARKET_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export function getDefaultSessionDate(now: Date = new Date()): string {
  const parts = marketDateFormatter.formatToParts(now);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);
  const date = new Date(Date.UTC(year, month - 1, day));

  while (!isMarketDayCandidate(date)) {
    date.setUTCDate(date.getUTCDate() - 1);
  }

  return date.toISOString().slice(0, 10);
}

export function getMarketCalendarDate(now: Date = new Date()): Date {
  const parts = marketDateFormatter.formatToParts(now);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);
  return new Date(Date.UTC(year, month - 1, day));
}

export function getEarliestSessionDate(now: Date = new Date()): Date {
  const date = getMarketCalendarDate(now);
  date.setUTCDate(date.getUTCDate() - (SESSION_DATE_LOOKBACK_DAYS - 1));
  return date;
}

export function isMarketDayCandidate(date: Date): boolean {
  const day = date.getUTCDay();
  return day >= 1 && day <= 5 && !isMarketHoliday(date);
}

export function isLiveMarketSession(sessionDate: string, now: Date = new Date()): boolean {
  const marketDate = getMarketCalendarDate(now);
  if (sessionDate !== marketDate.toISOString().slice(0, 10) || !isMarketDayCandidate(marketDate)) {
    return false;
  }

  const parts = marketTimeFormatter.formatToParts(now);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value);
  const minutesAfterMidnight = hour * 60 + minute;
  const marketCloseMinutes = getMarketSessionCloseMinutes(sessionDate);

  return (
    Number.isFinite(minutesAfterMidnight) &&
    minutesAfterMidnight >= MARKET_OPEN_MINUTES &&
    minutesAfterMidnight < marketCloseMinutes
  );
}

export function getMarketSessionCloseMinutes(sessionDate: string): number {
  const date = new Date(`${sessionDate}T00:00:00Z`);
  return isEarlyCloseSession(date) ? EARLY_MARKET_CLOSE_MINUTES : REGULAR_MARKET_CLOSE_MINUTES;
}

function isMarketHoliday(date: Date): boolean {
  const year = date.getUTCFullYear();
  const holidays = [
    observedFixedHoliday(year, 0, 1),
    observedFixedHoliday(year + 1, 0, 1),
    nthWeekdayOfMonth(year, 0, 1, 3),
    nthWeekdayOfMonth(year, 1, 1, 3),
    addUtcDays(easterSunday(year), -2),
    lastWeekdayOfMonth(year, 4, 1),
    ...(year >= 2022 ? [observedFixedHoliday(year, 5, 19)] : []),
    observedFixedHoliday(year, 6, 4),
    nthWeekdayOfMonth(year, 8, 1, 1),
    nthWeekdayOfMonth(year, 10, 4, 4),
    observedFixedHoliday(year, 11, 25),
  ];

  return holidays.some((holiday) => holiday.getTime() === date.getTime());
}

function isEarlyCloseSession(date: Date): boolean {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const weekday = date.getUTCDay();
  const dayAfterThanksgiving = addUtcDays(nthWeekdayOfMonth(year, 10, 4, 4), 1);

  return (
    date.getTime() === dayAfterThanksgiving.getTime() ||
    (month === 6 && day === 3 && weekday >= 1 && weekday <= 4) ||
    (month === 11 && day === 24 && weekday >= 1 && weekday <= 4)
  );
}

function observedFixedHoliday(year: number, month: number, day: number): Date {
  const date = new Date(Date.UTC(year, month, day));

  if (date.getUTCDay() === 6) {
    date.setUTCDate(date.getUTCDate() - 1);
  } else if (date.getUTCDay() === 0) {
    date.setUTCDate(date.getUTCDate() + 1);
  }

  return date;
}

function nthWeekdayOfMonth(year: number, month: number, weekday: number, nth: number): Date {
  const date = new Date(Date.UTC(year, month, 1));
  const offset = (weekday - date.getUTCDay() + 7) % 7;
  date.setUTCDate(1 + offset + (nth - 1) * 7);
  return date;
}

function lastWeekdayOfMonth(year: number, month: number, weekday: number): Date {
  const date = new Date(Date.UTC(year, month + 1, 0));
  const offset = (date.getUTCDay() - weekday + 7) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return date;
}

function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

function addUtcDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}
